import { useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import { appState } from "../../../state";
import { showToast } from "../../../toast";
import { fetchGameDecksApi } from "../../../gameApi";
import { pauseAskAnswerApi, pauseAskResumeApi, pauseAskSkipApi, pauseAskStartApi } from "../../../togetherApi";
import type { GameDecksResponse, PauseAndAskState, TogetherMediaType } from "../../../types";

type PickerStep = "menu" | "custom" | "spicy-intensity";

// A lightweight in-context Q&A layer over the paused video - deliberately
// NOT a route change or a full-screen takeover (the video stays visible,
// dimmed, behind this card). Reuses gameDecks.js's real deck metadata via
// the existing fetchGameDecksApi() (module-cached, same call GameRoot.tsx
// already makes for its own lobby) rather than hardcoding category labels
// that might drift from the real deck data.
export function PauseAndAskOverlay({ sessionId, pauseAndAsk }: { sessionId: string; pauseAndAsk: PauseAndAskState }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerStep, setPickerStep] = useState<PickerStep>("menu");
  const [decksMeta, setDecksMeta] = useState<GameDecksResponse | null>(null);
  const [customText, setCustomText] = useState("");
  const [answerText, setAnswerText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function openPicker(): void {
    setPickerOpen(true);
    setPickerStep("menu");
    setCustomText("");
    setError("");
    if (!decksMeta) fetchGameDecksApi().then(setDecksMeta);
  }

  // Purely local UI state until Start is actually pressed - no server call
  // happens just from opening/backing out of the picker.
  function closePicker(): void {
    setPickerOpen(false);
    setPickerStep("menu");
  }

  async function commitStart(source: "custom" | "random" | "questions" | "spicy", intensity?: number): Promise<void> {
    setBusy(true);
    setError("");
    // Same DOM lookup togetherSync.ts's sendNow() already uses to capture
    // the video's own live position for /sync - reused here so both
    // participants land on the exact position the initiator was at.
    const video = document.getElementById("mediaModalVideo") as HTMLVideoElement | null;
    const result = await pauseAskStartApi(sessionId, {
      source,
      intensity,
      customText: source === "custom" ? customText.trim() : undefined,
      position: video?.currentTime ?? 0,
      filename: appState.togetherSession?.filename || "",
      mediaType: (appState.togetherSession?.mediaType || "video") as TogetherMediaType,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error || "Failed to start.");
      return;
    }
    closePicker();
  }

  async function submitAnswer(): Promise<void> {
    const text = answerText.trim();
    if (!text) return;
    setBusy(true);
    const result = await pauseAskAnswerApi(sessionId, text);
    setBusy(false);
    if (!result.ok) {
      showToast(result.error || "Failed to send answer.", "error");
      return;
    }
    setAnswerText("");
  }

  async function handleResume(): Promise<void> {
    setBusy(true);
    await pauseAskResumeApi(sessionId);
    setBusy(false);
  }

  async function handleSkip(): Promise<void> {
    setBusy(true);
    await pauseAskSkipApi(sessionId);
    setBusy(false);
  }

  const showOverlay = pickerOpen || !!pauseAndAsk;
  const questionsDeck = decksMeta?.decks.find((d) => d.id === "questions");
  const hasSpicyDeck = !!decksMeta?.decks.find((d) => d.id === "spicy");

  return (
    <>
      <IconButton
        aria-label="Pause and ask a question"
        size="small"
        onClick={openPicker}
        disabled={!!pauseAndAsk}
        sx={{ fontSize: "1.15rem", p: 0.75 }}
      >
        <i className="bi bi-chat-square-heart" aria-hidden="true" />
      </IconButton>

      {showOverlay && (
        <div className="pause-ask-overlay">
          <div className="pause-ask-card">
            {!pauseAndAsk ? (
              pickerStep === "menu" ? (
                <>
                  <div className="pause-ask-title">Pause &amp; Ask</div>
                  <div className="pause-ask-subtitle">Ask something while we pause.</div>
                  {error && <div className="pause-ask-error">{error}</div>}
                  <div className="pause-ask-menu">
                    <button type="button" className="pause-ask-menu-item" onClick={() => setPickerStep("custom")} disabled={busy}>
                      ✍️ Ask my own question
                    </button>
                    <button type="button" className="pause-ask-menu-item" onClick={() => commitStart("random")} disabled={busy}>
                      🎲 Random Question
                    </button>
                    {questionsDeck && (
                      <button type="button" className="pause-ask-menu-item" onClick={() => commitStart("questions")} disabled={busy}>
                        {questionsDeck.emoji} {questionsDeck.label}
                      </button>
                    )}
                    {hasSpicyDeck && (
                      <button type="button" className="pause-ask-menu-item" onClick={() => setPickerStep("spicy-intensity")} disabled={busy}>
                        😈 Spicy
                      </button>
                    )}
                  </div>
                  <Button size="small" onClick={closePicker} disabled={busy}>
                    Cancel
                  </Button>
                </>
              ) : pickerStep === "custom" ? (
                <>
                  <div className="pause-ask-title">Ask your partner</div>
                  {error && <div className="pause-ask-error">{error}</div>}
                  <TextField
                    label="What's on your mind?"
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    disabled={busy}
                    slotProps={{ htmlInput: { maxLength: 4000 } }}
                  />
                  <div className="d-flex gap-2 justify-content-end mt-2">
                    <Button size="small" onClick={() => setPickerStep("menu")} disabled={busy}>
                      Back
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => commitStart("custom")}
                      disabled={busy || !customText.trim()}
                    >
                      Ask →
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="pause-ask-title">😈 Spicy</div>
                  {error && <div className="pause-ask-error">{error}</div>}
                  <div className="pause-ask-menu">
                    {(decksMeta?.intensityLevels || []).map((lvl) => (
                      <button
                        key={lvl.level}
                        type="button"
                        className="pause-ask-menu-item"
                        onClick={() => commitStart("spicy", lvl.level)}
                        disabled={busy}
                      >
                        {lvl.emoji} {lvl.label}
                      </button>
                    ))}
                  </div>
                  <Button size="small" onClick={() => setPickerStep("menu")} disabled={busy}>
                    Back
                  </Button>
                </>
              )
            ) : !pauseAndAsk.revealed ? (
              <>
                <div className="pause-ask-badge">{pauseAndAsk.category}</div>
                <div className="pause-ask-question">{pauseAndAsk.question}</div>
                {pauseAndAsk.myAnswered ? (
                  <div className="pause-ask-waiting">
                    <i className="bi bi-check-circle-fill" aria-hidden="true" /> Answer submitted
                    <br />
                    {pauseAndAsk.peerAnswered ? "Revealing…" : "Waiting for your partner…"}
                  </div>
                ) : (
                  <>
                    <TextField
                      label="Your answer"
                      value={answerText}
                      onChange={(e) => setAnswerText(e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      disabled={busy}
                      slotProps={{ htmlInput: { maxLength: 4000 } }}
                    />
                    <div className="d-flex gap-2 justify-content-end mt-2">
                      <Button size="small" onClick={handleSkip} disabled={busy}>
                        Skip
                      </Button>
                      <Button size="small" variant="contained" color="primary" onClick={submitAnswer} disabled={busy || !answerText.trim()}>
                        Send →
                      </Button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="pause-ask-badge">Both answered ❤️</div>
                <div className="pause-ask-question">{pauseAndAsk.question}</div>
                <div className="pause-ask-reveal">
                  <div className="pause-ask-reveal-row">
                    <div className="pause-ask-reveal-who">You</div>
                    <div className="pause-ask-reveal-text">&ldquo;{pauseAndAsk.answers.mine}&rdquo;</div>
                  </div>
                  <div className="pause-ask-reveal-row">
                    <div className="pause-ask-reveal-who">Partner</div>
                    <div className="pause-ask-reveal-text">&ldquo;{pauseAndAsk.answers.peer}&rdquo;</div>
                  </div>
                </div>
                <Button fullWidth variant="contained" color="primary" onClick={handleResume} disabled={busy}>
                  Continue Watching ▶
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
